/**
 * Контроль доступа к разделам админки по роли администратора.
 *
 * Единый источник правды: используется и в route-middleware (гард доступа),
 * и в сайдбаре (фильтрация пунктов меню). Источник роли — поле admin_users.role
 * (приходит в JWT), см. useAdminSessionManager.getAdminRole().
 *
 * Бэкенд дублирует эти ограничения через RolesGuard (@Roles) на закрытых
 * контроллерах — фронт-гард лишь скрывает недоступное и не пускает по URL.
 */

export type AdminRole = 'admin' | 'super_admin' | 'manager'

/**
 * Роли с полным доступом ко всем разделам админки.
 * Любая роль вне этого списка считается ограниченной (см. MANAGER_ALLOWED_PREFIXES).
 */
export const FULL_ACCESS_ADMIN_ROLES: readonly AdminRole[] = ['admin', 'super_admin']

/**
 * Разделы, доступные роли `manager`.
 * Значения — префиксы путей (без учёта локали и завершающего сегмента/`/:id`).
 */
export const MANAGER_ALLOWED_PREFIXES: readonly string[] = [
  '/administration/orders',
  '/administration/reviews',
  '/administration/workload-control',
  '/administration/news-slider',
  '/administration/referral',
  '/administration/promocodes',
  '/administration/retention',
  '/administration/combos',
  '/administration/productGroup',
  '/administration/excludeIngredients',
  '/administration/addon',
  '/administration/tags',
  '/administration/categories',
  '/administration/products',
  // Разбор карточек одного блюда по филиалам — та же работа с каталогом, что и
  // «Товары»: без неё менеджер не может довести до конца заведение блюда на точки.
  '/administration/dish-groups',
  '/administration/analytics',
  // Воронки — та же сводная аналитика, что уже открыта менеджеру, и притом более
  // безобидная: ни выручки, ни персональных данных, только доли по шагам.
  '/administration/funnels',
  // Мобильная аналитика — аудитория приложения без денег и персональных данных,
  // ограничивать её от менеджера незачем.
  '/administration/mobile-analytics',
  // Яндекс Доставка — операционка с курьерами (вызвать, отменить, телефон курьера),
  // без неё менеджер не доведёт заказ до двери. Вкладка настроек с токеном скрыта
  // на клиенте по роли, а на бэке закрыта @Roles('admin','super_admin').
  '/administration/yandex-delivery',
  '/administration/knowledge-base',
]

/**
 * Страница входа в админку.
 */
export const ADMIN_LOGIN_PATH = '/administration/home'

/**
 * Страница входа в админку доступна всем (в т.ч. без сессии и любой роли).
 */
export const ADMIN_PUBLIC_PREFIXES: readonly string[] = [ADMIN_LOGIN_PATH]

/**
 * Куда отправлять ограниченную роль по умолчанию (первый доступный раздел).
 */
export const MANAGER_DEFAULT_PATH = '/administration/orders'

/**
 * Нормализует путь: убирает префикс локали (`/en`, `/ru`...) и хвостовой слеш.
 */
function normalizeAdminPath(path: string): string {
  const withoutLocale = path.replace(/^\/[a-z]{2}(?=\/administration)/i, '')
  return withoutLocale.replace(/\/+$/, '') || withoutLocale
}

function matchesPrefix(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`)
}

/**
 * Полный ли доступ у роли (только admin/super_admin).
 */
export function hasFullAdminAccess(role: string | null | undefined): boolean {
  return !!role && FULL_ACCESS_ADMIN_ROLES.includes(role as AdminRole)
}

/**
 * Роль ограниченная, но известная — ей полагается allow-list.
 */
function isRestrictedAdminRole(role: string | null | undefined): boolean {
  return role === 'manager'
}

/**
 * Может ли роль открыть указанный admin-путь.
 * - Страница входа — доступна всем.
 * - Полные роли — да, всегда.
 * - `manager` — только по allow-list (MANAGER_ALLOWED_PREFIXES).
 * - Роль не распознана — нет: неизвестное не приравниваем к разрешённому.
 *
 * Последнее правило важно: `getAdminRole()` возвращает null и когда payload токена не
 * разобрался. Раньше такая сессия проваливалась в проверку allow-list и получала все
 * 15 разделов менеджера вместо нуля.
 */
export function canAccessAdminPath(role: string | null | undefined, path: string): boolean {
  const normalized = normalizeAdminPath(path)

  if (ADMIN_PUBLIC_PREFIXES.some((p) => matchesPrefix(normalized, p))) return true
  if (hasFullAdminAccess(role)) return true
  if (!isRestrictedAdminRole(role)) return false

  return MANAGER_ALLOWED_PREFIXES.some((p) => matchesPrefix(normalized, p))
}

/**
 * Первый доступный роли admin-путь (для редиректа при отказе в доступе).
 *
 * Нераспознанную роль отправляем на страницу входа: любой другой раздел ей теперь
 * закрыт, и редирект туда зациклился бы.
 */
export function defaultAdminPathForRole(role: string | null | undefined): string {
  if (hasFullAdminAccess(role)) return '/administration'
  if (isRestrictedAdminRole(role)) return MANAGER_DEFAULT_PATH

  return ADMIN_LOGIN_PATH
}
